import React, { useState } from 'react';
import {
  X, Play, BookmarkPlus, AlertTriangle, CheckCircle,
  Edit3, Eye, Loader2, Copy, Check
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface GeneratedTransform {
  fn_name: string;
  code: string;
  params_schema: Record<string, string>;
  explanation: string;
  estimated_match_rates: Record<string, string>;
  warnings: string[];
}

interface CodeReviewPanelProps {
  transform: GeneratedTransform;
  rowCount: number;
  dfPath: string;
  sessionId: number | null;
  engagementId: string;
  promptSummary: string;
  onClose: () => void;
  onExecuted: (result: any) => void;
}

const CodeReviewPanel: React.FC<CodeReviewPanelProps> = ({
  transform, rowCount, dfPath, sessionId, engagementId, promptSummary, onClose, onExecuted,
}) => {
  const [code, setCode] = useState(transform.code);
  const [isEditing, setIsEditing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [libraryLabel, setLibraryLabel] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [copied, setCopied] = useState(false);

  const wasEdited = code !== transform.code;
  const codeSource = wasEdited ? 'ai_generated_human_edited' : 'ai_generated';

  const handleExecute = async () => {
    setExecuting(true);
    try {
      const response = await api.post('/api/custom-transform/execute', {
        code,
        fn_name: transform.fn_name,
        code_source: codeSource,
        prompt_summary: promptSummary,
        explanation: transform.explanation,
        session_id: sessionId,
        engagement_id: engagementId,
        dfPath,
        params: {},
      }, { timeout: 180000 });

      if (response.data.success) {
        toast.success('Transformation executed successfully');
        onExecuted(response.data.data);
      } else {
        const warnings = response.data.data?.warnings || [];
        toast.error(warnings[0] || response.data.error || 'Execution failed');
        onExecuted(response.data.data || { status: 'error' });
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const handleSaveToLibrary = async (transformationId?: string) => {
    if (!libraryLabel.trim()) {
      toast.error('Please enter a label');
      return;
    }

    setSaving(true);
    try {
      const execResponse = await api.post('/api/custom-transform/execute', {
        code,
        fn_name: transform.fn_name,
        code_source: codeSource,
        prompt_summary: promptSummary,
        explanation: transform.explanation,
        session_id: sessionId,
        engagement_id: engagementId,
        dfPath,
        params: {},
      }, { timeout: 180000 });

      const tId = execResponse.data.data?.transformation_id;
      if (!tId) {
        toast.error('Must execute before saving to library');
        setSaving(false);
        return;
      }

      await api.post('/api/custom-transform/library/save', {
        transformation_id: tId,
        label: libraryLabel.trim(),
        description: transform.explanation,
        engagement_id: engagementId || null,
      });

      toast.success('Saved to transformation library');
      setShowSaveForm(false);
      onExecuted(execResponse.data.data);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-800">Review Generated Code</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Function: <span className="font-mono text-violet-600">{transform.fn_name}</span>
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">What This Does</h3>
            <p className="text-sm text-gray-700">{transform.explanation}</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Code</h3>
              <div className="flex items-center gap-1.5">
                <button onClick={handleCopy} className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                  {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-colors ${
                    isEditing ? 'bg-violet-100 text-violet-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {isEditing ? <Eye className="w-3 h-3" /> : <Edit3 className="w-3 h-3" />}
                  {isEditing ? 'Preview' : 'Edit'}
                </button>
              </div>
            </div>

            {isEditing ? (
              <textarea
                value={code}
                onChange={e => setCode(e.target.value)}
                className="w-full h-64 px-4 py-3 font-mono text-xs bg-gray-900 text-green-300 rounded-xl border border-gray-700 focus:ring-2 focus:ring-violet-500 resize-y"
                spellCheck={false}
              />
            ) : (
              <pre className="w-full h-64 px-4 py-3 font-mono text-xs bg-gray-900 text-green-300 rounded-xl border border-gray-700 overflow-auto scrollbar-thin">
                {code}
              </pre>
            )}

            {wasEdited && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <Edit3 className="w-3 h-3" />
                Code has been edited. Will be logged as &quot;ai_generated_human_edited&quot;.
              </p>
            )}
          </div>

          {transform.estimated_match_rates && Object.keys(transform.estimated_match_rates).length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Estimated Impact (on {rowCount.toLocaleString()} rows)
              </h3>
              <div className="space-y-2.5">
                {Object.entries(transform.estimated_match_rates).map(([col, rate]) => {
                  const rateStr = String(rate);
                  const numMatch = rateStr.match(/(\d+)/);
                  const pct = numMatch ? Math.min(parseInt(numMatch[1]), 100) : 0;
                  const rows = Math.round(rowCount * (pct / 100));
                  return (
                    <div key={col} className="flex items-center gap-3">
                      <span className="text-xs font-mono text-gray-600 w-40 truncate" title={col}>{col}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-2.5 min-w-[80px]">
                        <div
                          className="bg-violet-500 h-2.5 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-32 text-right whitespace-nowrap">
                        {pct}% (~{rows.toLocaleString()} rows)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {transform.warnings?.length > 0 && (
            <div className="space-y-1.5">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Warnings</h3>
              {transform.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-amber-700">{w}</span>
                </div>
              ))}
            </div>
          )}

        </div>

        {showSaveForm && (
          <div className="px-5 py-3 border-t border-violet-200 bg-violet-50">
            <h3 className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-2">Save to Library</h3>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={libraryLabel}
                onChange={e => setLibraryLabel(e.target.value)}
                placeholder="Label for this transformation..."
                className="flex-1 px-3 py-2 text-sm border border-violet-200 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white"
                autoFocus
              />
              <button
                onClick={() => handleSaveToLibrary()}
                disabled={saving || !libraryLabel.trim()}
                className="px-4 py-2 text-xs font-bold bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Saving...</> : 'Save & Execute'}
              </button>
              <button
                onClick={() => setShowSaveForm(false)}
                className="px-3 py-2 text-xs text-gray-500 hover:bg-violet-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
          <button
            onClick={() => setShowSaveForm(!showSaveForm)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-colors ${
              showSaveForm
                ? 'text-violet-700 bg-violet-100 border border-violet-300'
                : 'text-violet-600 bg-violet-50 border border-violet-200 hover:bg-violet-100'
            }`}
          >
            <BookmarkPlus className="w-3.5 h-3.5" />
            Save to library as...
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Discard
            </button>
            <button
              onClick={handleExecute}
              disabled={executing}
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-bold bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm disabled:opacity-50"
            >
              {executing ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Executing...</>
              ) : (
                <><Play className="w-3.5 h-3.5" /> Approve & Run</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeReviewPanel;
